import React, { useEffect, createElement } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../hooks/useLanguage';
import { Language } from '../types';
interface SEOProps {
  title: string;
  description: string;
  image?: string;
  type?: 'website' | 'article';
  keywords?: string;
}
// Map language codes to full locale codes for Open Graph
const localeMap: Record<Language, string> = {
  en: 'en_US',
  id: 'id_ID',
  ms: 'ms_MY',
  th: 'th_TH',
  vi: 'vi_VN',
  fil: 'fil_PH',
  km: 'km_KH',
  lo: 'lo_LA',
  my: 'my_MM'
};
// Map language codes to country names for structured data
const countryMap: Record<Language, string> = {
  en: 'International',
  id: 'Indonesia',
  ms: 'Malaysia',
  th: 'Thailand',
  vi: 'Vietnam',
  fil: 'Philippines',
  km: 'Cambodia',
  lo: 'Laos',
  my: 'Myanmar'
};
export function SEO({
  title,
  description,
  image = 'https://cdn.magicpatterns.com/uploads/uXx3yCsHjduce19DFGonX9/yseali-cybersafe-logo-desktop.png',
  type = 'website',
  keywords = 'scam prevention, fraud protection, ASEAN, cybersecurity, online safety, report scam, anti-fraud'
}: SEOProps) {
  const {
    language,
    supportedLanguages
  } = useLanguage();
  const location = useLocation();
  // Base URL - in production this would be an env var
  const baseUrl = window.location.origin;
  const currentUrl = `${baseUrl}${location.pathname}`;
  // Construct full title
  const siteName = 'genZAFE.org';
  const fullTitle = `${title} | ${siteName}`;
  useEffect(() => {
    // Update document title
    document.title = fullTitle;
    // Update HTML lang attribute
    document.documentElement.lang = language;
    // Helper to update or create meta tag
    const updateMeta = (name: string, content: string, attribute: 'name' | 'property' = 'name') => {
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };
    // Helper to update or create link tag
    const updateLink = (rel: string, href: string, hreflang?: string) => {
      let selector = `link[rel="${rel}"]`;
      if (hreflang) {
        selector += `[hreflang="${hreflang}"]`;
      }
      let element = document.querySelector(selector);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        if (hreflang) element.setAttribute('hreflang', hreflang);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };
    // Helper to add or update JSON-LD structured data
    const updateJsonLd = (data: object) => {
      let script = document.querySelector('script[type="application/ld+json"]');
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };
    // ===== Standard Meta Tags =====
    updateMeta('description', description);
    updateMeta('keywords', keywords);
    updateMeta('author', 'genZAFE - ASEAN Anti-Scam Initiative');
    updateMeta('robots', 'index, follow');
    updateMeta('viewport', 'width=device-width, initial-scale=1');
    updateMeta('theme-color', '#1e3a5f');
    // ===== Open Graph / Facebook =====
    updateMeta('og:type', type, 'property');
    updateMeta('og:url', currentUrl, 'property');
    updateMeta('og:title', fullTitle, 'property');
    updateMeta('og:description', description, 'property');
    updateMeta('og:image', image, 'property');
    updateMeta('og:image:width', '1200', 'property');
    updateMeta('og:image:height', '630', 'property');
    updateMeta('og:site_name', siteName, 'property');
    updateMeta('og:locale', localeMap[language] || 'en_US', 'property');
    // Add alternate locales for Open Graph
    supportedLanguages.forEach((lang: Language) => {
      if (lang !== language) {
        updateMeta(`og:locale:alternate`, localeMap[lang] || lang, 'property');
      }
    });
    // ===== Twitter Card =====
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:url', currentUrl);
    updateMeta('twitter:title', fullTitle);
    updateMeta('twitter:description', description);
    updateMeta('twitter:image', image);
    updateMeta('twitter:site', '@genZAFE');
    // ===== Canonical URL =====
    updateLink('canonical', currentUrl);
    // ===== Hreflang Tags for International SEO =====
    const pathSegments = location.pathname.split('/');
    const pathAfterLang = pathSegments.slice(2).join('/');
    // Add hreflang for each supported language
    supportedLanguages.forEach((lang: Language) => {
      const langPath = pathAfterLang ? `/${lang}/${pathAfterLang}` : `/${lang}`;
      const langUrl = `${baseUrl}${langPath}`;
      updateLink('alternate', langUrl, lang);
    });
    // Add x-default hreflang (points to English version)
    const defaultPath = pathAfterLang ? `/en/${pathAfterLang}` : '/en';
    const defaultUrl = `${baseUrl}${defaultPath}`;
    updateLink('alternate', defaultUrl, 'x-default');
    // ===== JSON-LD Structured Data =====
    const structuredData = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: siteName,
      alternateName: 'Generation Zero-risk ASEAN Fraud Empowerment',
      url: baseUrl,
      description: 'ASEAN Anti-Scam Initiative - Protecting Southeast Asian citizens from online fraud',
      inLanguage: supportedLanguages.map((lang: Language) => localeMap[lang] || lang),
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${baseUrl}/en/check-scam?q={search_term_string}`
        },
        'query-input': 'required name=search_term_string'
      },
      publisher: {
        '@type': 'Organization',
        name: 'genZAFE',
        logo: {
          '@type': 'ImageObject',
          url: image
        },
        areaServed: supportedLanguages.map((lang: Language) => ({
          '@type': 'Country',
          name: countryMap[lang] || 'ASEAN'
        }))
      }
    };
    updateJsonLd(structuredData);
  }, [fullTitle, description, image, type, keywords, currentUrl, language, supportedLanguages, baseUrl, location.pathname]);
  return null;
}